/**
 * System Settings Page - Systemeinstellungen
 * Optimiert für alle Geräte mit responsivem Design
 */

import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Stack,
  Grid,
  Card,
  CardContent,
  Button,
  useTheme,
  useMediaQuery,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Modal,
  List,
  ListItem,
  ListItemText,
  Chip,
} from "@mui/material";
import {
  Settings as SettingsIcon,
  Storage as StorageIcon,
  Memory as MemoryIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  DeleteSweep as DeleteSweepIcon,
  PowerSettingsNew as PowerSettingsNewIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { useApiClient } from "../../hooks/useApiClient";
import {
  SystemInfo,
  SystemActionResult,
  LogEntry,
} from "../../types/api-types";
import PresenceCameraSettings from "../../components/settings/PresenceCameraSettings";
import ZoneEditor from "../../components/settings/ZoneEditor";

const SystemSettingsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const apiClient = useApiClient();

  const [systemInfo, setSystemInfo] = useState<SystemInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    action: string;
  }>({
    open: false,
    title: "",
    message: "",
    action: "",
  });
  const [logsModal, setLogsModal] = useState<{
    open: boolean;
    logs: LogEntry[];
    loading: boolean;
  }>({
    open: false,
    logs: [],
    loading: false,
  });

  // Load system information
  const loadSystemInfo = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.getSystemInfo();
      setSystemInfo(data);
    } catch (err: any) {
      setError(err.message || "Fehler beim Laden der Systeminformationen");
      console.error("Error loading system info:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSystemInfo();
  }, []);

  const handleAction = async (action: string) => {
    try {
      setActionLoading(action);
      setError(null);
      setSuccess(null);

      let result: SystemActionResult;

      switch (action) {
        case "backup":
          result = await apiClient.createSystemBackup();
          // Automatischer Download nach erfolgreichem Backup
          if (result.success && result.details?.fileName) {
            try {
              await apiClient.downloadBackup(result.details.fileName);
              setSuccess(`${result.message} - Download gestartet`);
            } catch (downloadError: any) {
              setSuccess(result.message);
              setError(`Download fehlgeschlagen: ${downloadError.message}`);
            }
          }
          break;
        case "clearCache":
          result = await apiClient.clearSystemCache();
          break;
        case "restart":
          result = await apiClient.prepareSystemRestart();
          break;
        case "viewLogs":
          await handleViewLogs();
          return; // Früher Exit, da handleViewLogs eigene Behandlung hat
        default:
          throw new Error("Unbekannte Aktion");
      }

      if (result.success) {
        setSuccess(result.message);
        // Nach erfolgreichen Aktionen System-Info aktualisieren
        if (action === "backup" || action === "clearCache") {
          await loadSystemInfo();
        }
      } else {
        setError(result.error || "Aktion fehlgeschlagen");
      }
    } catch (err: any) {
      setError(err.message || "Fehler bei der Systemaktion");
    } finally {
      setActionLoading(null);
      setConfirmDialog({ open: false, title: "", message: "", action: "" });
    }
  };

  const handleViewLogs = async () => {
    try {
      setLogsModal({ open: true, logs: [], loading: true });
      const logs = await apiClient.getSystemLogs(50);
      setLogsModal({ open: true, logs, loading: false });
    } catch (err: any) {
      setError(err.message || "Fehler beim Laden der Logs");
      setLogsModal({ open: false, logs: [], loading: false });
    }
  };

  const showConfirmDialog = (action: string) => {
    const dialogs = {
      backup: {
        title: "Backup erstellen",
        message:
          "Möchten Sie jetzt ein Datenbank-Backup erstellen? Das Backup wird automatisch heruntergeladen.",
      },
      clearCache: {
        title: "Cache leeren",
        message:
          "Möchten Sie den System-Cache leeren? Dies kann die Performance kurzzeitig beeinträchtigen.",
      },
      restart: {
        title: "System neu starten",
        message:
          "Möchten Sie das System für einen Neustart vorbereiten? Aktive Vermietungen werden überprüft.",
      },
      viewLogs: {
        title: "System-Logs anzeigen",
        message: "Möchten Sie die aktuellen System-Logs anzeigen?",
      },
    };

    const dialog = dialogs[action as keyof typeof dialogs];
    if (dialog) {
      setConfirmDialog({
        open: true,
        title: dialog.title,
        message: dialog.message,
        action,
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "ok":
      case "healthy":
        return "success.main";
      case "warning":
        return "warning.main";
      case "critical":
        return "error.main";
      default:
        return "info.main";
    }
  };

  const getLogLevelColor = (level: string) => {
    switch (level) {
      case "ERROR":
        return "error";
      case "WARN":
        return "warning";
      case "INFO":
        return "info";
      case "DEBUG":
        return "default";
      default:
        return "default";
    }
  };

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: { xs: 1, md: 3 },
        px: { xs: 0.5, md: 3 },
        maxWidth: { xs: "100%", md: "xl" },
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, md: 3 },
          mb: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          borderRadius: 2,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              component="h1"
              gutterBottom
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontWeight: "bold",
                mb: 1,
              }}
            >
              <SettingsIcon fontSize={isMobile ? "medium" : "large"} />
              Systemeinstellungen
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Überwachen und verwalten Sie Ihr Mietwerkstatt-System
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={loadSystemInfo}
            disabled={loading}
            sx={{
              bgcolor: "rgba(255,255,255,0.2)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
              borderRadius: 2,
            }}
          >
            Aktualisieren
          </Button>
        </Stack>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3, borderRadius: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Success Snackbar */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>

      {/* Loading State */}
      {loading && !systemInfo && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* System Information Cards */}
      {systemInfo && (
        <Grid container spacing={3}>
          {/* System Info */}
          <Grid item xs={12} lg={6}>
            <Card
              sx={{ borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}
            >
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  fontWeight="bold"
                  color="primary"
                >
                  🔧 System-Information
                </Typography>

                <Stack spacing={2}>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Version:
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {systemInfo.version}
                    </Typography>
                  </Box>

                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Node.js:
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {systemInfo.nodeVersion}
                    </Typography>
                  </Box>

                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Platform:
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {systemInfo.platform}
                    </Typography>
                  </Box>

                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Betriebszeit:
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {systemInfo.uptime}
                    </Typography>
                  </Box>

                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Letztes Backup:
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {systemInfo.lastBackup || "Kein Backup vorhanden"}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Performance & Business Info */}
          <Grid item xs={12} lg={6}>
            <Card
              sx={{ borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}
            >
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  fontWeight="bold"
                  color="primary"
                >
                  💾 Performance & Business
                </Typography>

                <Stack spacing={2}>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Datenbank-Größe:
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {systemInfo.databaseSize}
                    </Typography>
                  </Box>

                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Speichernutzung:
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {systemInfo.memoryUsage.used} /{" "}
                      {systemInfo.memoryUsage.total} (
                      {systemInfo.memoryUsage.percentage}%)
                    </Typography>
                  </Box>

                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      CPU-Auslastung:
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {systemInfo.systemLoad.cpu}%
                    </Typography>
                  </Box>

                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Aktive Benutzer:
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {systemInfo.activeUsers}
                    </Typography>
                  </Box>

                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Aktive Vermietungen:
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {systemInfo.activeRentals}
                    </Typography>
                  </Box>

                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Vermietungen heute:
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {systemInfo.todayRentals}
                    </Typography>
                  </Box>

                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Gesamtumsatz:
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="medium"
                      color="success.main"
                    >
                      {systemInfo.totalRevenue}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* System Actions */}
          <Grid item xs={12}>
            <Card
              sx={{ borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}
            >
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  fontWeight="bold"
                  color="primary"
                >
                  🔧 System-Aktionen
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={
                        actionLoading === "backup" ? (
                          <CircularProgress size={20} />
                        ) : (
                          <DownloadIcon />
                        )
                      }
                      onClick={() => showConfirmDialog("backup")}
                      disabled={!!actionLoading}
                      sx={{ borderRadius: 2, py: 2 }}
                    >
                      Backup erstellen
                    </Button>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={
                        actionLoading === "clearCache" ? (
                          <CircularProgress size={20} />
                        ) : (
                          <DeleteSweepIcon />
                        )
                      }
                      onClick={() => showConfirmDialog("clearCache")}
                      disabled={!!actionLoading}
                      sx={{ borderRadius: 2, py: 2 }}
                    >
                      Cache leeren
                    </Button>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<InfoIcon />}
                      onClick={() => showConfirmDialog("viewLogs")}
                      disabled={!!actionLoading}
                      sx={{ borderRadius: 2, py: 2 }}
                    >
                      Logs anzeigen
                    </Button>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="warning"
                      startIcon={
                        actionLoading === "restart" ? (
                          <CircularProgress size={20} />
                        ) : (
                          <PowerSettingsNewIcon />
                        )
                      }
                      onClick={() => showConfirmDialog("restart")}
                      disabled={!!actionLoading}
                      sx={{ borderRadius: 2, py: 2 }}
                    >
                      System vorbereiten
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Additional System Info */}
          <Grid item xs={12}>
            <Card
              sx={{ borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}
            >
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  fontWeight="bold"
                  color="primary"
                >
                  📊 System-Status
                </Typography>

                <Alert
                  severity={
                    systemInfo.memoryUsage.percentage > 80 ? "warning" : "info"
                  }
                  sx={{ mb: 2, borderRadius: 2 }}
                >
                  <Typography variant="body2">
                    <strong>Systemstatus:</strong>{" "}
                    {systemInfo.memoryUsage.percentage > 80
                      ? "Hohe Speichernutzung erkannt"
                      : "Alle Dienste laufen normal"}
                    .
                    {systemInfo.activeRentals > 0 &&
                      ` ${systemInfo.activeRentals} aktive Vermietungen.`}
                  </Typography>
                </Alert>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2, bgcolor: "grey.50", borderRadius: 2 }}>
                      <Typography
                        variant="subtitle2"
                        gutterBottom
                        color="primary"
                      >
                        🔄 Automatische Backups
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Tägliche Backups um 02:00 Uhr.
                        {systemInfo.lastBackup
                          ? ` Letztes Backup: ${systemInfo.lastBackup}`
                          : " Noch kein Backup erstellt."}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2, bgcolor: "grey.50", borderRadius: 2 }}>
                      <Typography
                        variant="subtitle2"
                        gutterBottom
                        color="primary"
                      >
                        📈 Leistung
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        CPU: {systemInfo.systemLoad.cpu}% | RAM:{" "}
                        {systemInfo.memoryUsage.percentage}% | DB:{" "}
                        {systemInfo.databaseSize}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Box sx={{ mt: 3 }}>
        <PresenceCameraSettings />
      </Box>

      <Box sx={{ mt: 3 }}>
        <ZoneEditor />
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
            disabled={!!actionLoading}
          >
            Abbrechen
          </Button>
          <Button
            onClick={() => handleAction(confirmDialog.action)}
            variant="contained"
            disabled={!!actionLoading}
            startIcon={
              actionLoading === confirmDialog.action ? (
                <CircularProgress size={20} />
              ) : undefined
            }
          >
            {actionLoading === confirmDialog.action
              ? "Wird ausgeführt..."
              : "Bestätigen"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Logs Modal */}
      <Modal
        open={logsModal.open}
        onClose={() => setLogsModal({ open: false, logs: [], loading: false })}
        aria-labelledby="logs-modal-title"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", md: "80%" },
            maxWidth: "1000px",
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            p: { xs: 2, md: 4 },
            maxHeight: "80vh",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography
            id="logs-modal-title"
            variant="h6"
            component="h2"
            gutterBottom
          >
            🔍 System-Logs
          </Typography>

          {logsModal.loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ flexGrow: 1, overflow: "auto" }}>
              {logsModal.logs.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: "center", py: 4 }}
                >
                  Keine Logs verfügbar
                </Typography>
              ) : (
                <List dense>
                  {logsModal.logs.map((log, index) => (
                    <ListItem key={index} divider>
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 0.5,
                            }}
                          >
                            <Chip
                              label={log.level}
                              size="small"
                              color={getLogLevelColor(log.level) as any}
                              variant="outlined"
                            />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {new Date(log.timestamp).toLocaleString("de-DE")}
                            </Typography>
                            {log.source && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                [{log.source}]
                              </Typography>
                            )}
                          </Box>
                        }
                        secondary={
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}
                          >
                            {log.message}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}

          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button
              onClick={() =>
                setLogsModal({ open: false, logs: [], loading: false })
              }
              variant="contained"
            >
              Schließen
            </Button>
          </Box>
        </Box>
      </Modal>
    </Container>
  );
};

export default SystemSettingsPage;
